// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import useOrderEvents from "@/hooks/useOrderEvents";
import usePermission from "@/hooks/usePermission";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import type { TyApiResponse, TyOrder, TyOrderStatus, TyOrderType } from "@/types";
import Skeleton from "@/UI/atoms/Skeleton";
import OrderCard from "@/UI/cards/OrderCard";
import ActionsBar from "@/UI/components/ActionsBar";
import OrdersLive from "@/UI/components/OrdersLive";
import FilterMultiSelectInput from "@/UI/inputs/FilterMultiSelectInput";
import FilterSearchInput from "@/UI/inputs/FilterSearchInput";
import FilterSelectInput from "@/UI/inputs/FilterSelectInput";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import MoreActionsPopup from "@/UI/popups/MoreActionsPopup";
import OrderEditPopup from "@/UI/popups/OrderEditPopup";
import OrderPreviewPopup from "@/UI/popups/OrderPreviewPopup";
import { armOrderChime, playOrderChime, playOrderUpdateChime } from "@/utils";
import "./styles.css";

// --- Constants ----------------------------------------------------------------------------------

/**
 * The bar's views: where the order is going, not how far along it is.
 *
 * A counter works one channel at a time — the delivery driver is waiting, the room is filling up —
 * and a status is a stage every one of those channels passes through. Status went to the filters,
 * where it can be combined with a date instead of replacing the view.
 */
const TABS: ("all" | TyOrderType)[] = ["all", "delivery", "pickup", "table"];

/** Remembered per device, not per account: it is a decision about this screen in this shop. */
const LIVE_KEY = "orders-live";

const readLive = (): boolean => {
    try {
        return localStorage.getItem(LIVE_KEY) !== "off";
    } catch {
        // A browser with storage blocked still gets the default rather than a blank screen.
        return true;
    }
};

/** Asked once per device, the first time this screen is opened. `null` means nobody has answered. */
const SOUND_KEY = "orders-sound";

const readSound = (): boolean | null => {
    try {
        const saved = localStorage.getItem(SOUND_KEY);
        return saved === null ? null : saved === "on";
    } catch {
        return null;
    }
};

const writeSound = (isOn: boolean) => {
    try {
        localStorage.setItem(SOUND_KEY, isOn ? "on" : "off");
    } catch {
        // Not being able to remember the choice is no reason to refuse it for this session.
    }
};

/** Only the digits, the way the API matches a phone — so "012…" finds a number saved as "+2012…". */
const digitsOf = (value: string): string => value.replace(/\D/g, "").replace(/^0+/, "");

// --- Types --------------------------------------------------------------------------------------

/**
 * Several statuses and several branches at once, because that is how a counter watches a service:
 * "being made and ready, in Maadi". The two searches are separate fields on purpose — an order
 * number is exact and a phone is a fragment, and one box made "012" match the order numbered 12.
 */
type TyFilters = { statuses: string[]; branches: string[]; range: string; number: string; phone: string };

const NO_FILTERS: TyFilters = { statuses: [], branches: [], range: "", number: "", phone: "" };

// --- Logic --------------------------------------------------------------------------------------

/**
 * The store's orders, newest first.
 *
 * Only orders sent through the system reach this list. An order placed over WhatsApp is recorded but
 * deliberately withheld — tapping that button leaves the browser and nothing reports back, so showing
 * one here would put an order in front of a store that may never have arrived.
 *
 * The cards are a summary and nothing more: opening one is what gives it the room to be worked on,
 * and the one that is open keeps the brand ring while the rest of the list steps back. Every card is
 * the same height whatever the order holds, so the list stays scannable at a busy counter.
 */
export default function OrdersScreen() {
    const t = useTranslation();
    const { addComp, removeComp } = usePopup();
    const { canEdit } = usePermission();

    const editable = canEdit("orders");

    const [tab, setTab] = useState<"all" | TyOrderType>("all");
    const [filters, setFilters] = useState<TyFilters>(NO_FILTERS);
    /** What the bar is holding while it is open. Nothing is sent until the tick is pressed. */
    const [draft, setDraft] = useState<TyFilters>(NO_FILTERS);
    const [isFiltering, setIsFiltering] = useState(false);

    const [page, setPage] = useState(1);
    /** Kept here rather than replaced on every page, so "load more" appends instead of jumping. */
    const [orders, setOrders] = useState<TyOrder[]>([]);
    const [total, setTotal] = useState(0);
    /** The one being worked on — it keeps the ring while every other card steps back. */
    const [activeId, setActiveId] = useState<string | null>(null);

    const [isLive, setIsLive] = useState(readLive);
    const [isSoundOn, setIsSoundOn] = useState<boolean | null>(readSound);
    /**
     * Orders that arrived while this list was open. Held **outside** the list on purpose: a list that
     * grows and re-sorts itself under a counter who is reading it is worse than one that is a minute
     * old. They are counted, announced, and only put in the list when someone asks for them.
     */
    const [pending, setPending] = useState<TyOrder[]>([]);
    /** The socket was down for a while. Whatever arrived then cannot be counted, only admitted to. */
    const [isCountLost, setIsCountLost] = useState(false);
    /** On screen, but no longer part of the list the filters asked for. Dimmed, not removed. */
    const [staleIds, setStaleIds] = useState<string[]>([]);

    const queryFor = (nextPage: number) => ({
        page: nextPage,
        ...(tab === "all" ? {} : { type: tab }),
        ...(filters.statuses.length ? { status: filters.statuses.join(",") } : {}),
        ...(filters.branches.length ? { branch: filters.branches.join(",") } : {}),
        ...(filters.range ? { range: filters.range } : {}),
        ...(filters.number ? { number: filters.number } : {}),
        ...(filters.phone ? { phone: filters.phone } : {}),
    });

    const { data, loading, error, invoke } = useFetch<TyApiResponse<{ orders: TyOrder[]; total: number; page: number }>>("/orders", {
        query: queryFor(1),
    });

    /* The branches this client may narrow to, which is not the same as the store's branches — it is
       answered under `/orders` so staff who never manage branches can still filter by them. */
    const { data: branchData } = useFetch<TyApiResponse<{ branches: { _id: string; name: string }[] }>>("/orders/branches");

    /**
     * Whether an order belongs in the list as it is narrowed right now. Three equality checks and two
     * searches — the same fields the API was asked for, which is why this can be trusted to say that
     * a card has left the list without asking the server.
     *
     * The date range is deliberately not checked: it is a window on when an order was placed, and
     * that never changes. A new order always falls inside one.
     */
    const fitsView = (order: TyOrder): boolean => {
        if (tab !== "all" && order.type !== tab) return false;
        if (filters.statuses.length && !filters.statuses.includes(order.status)) return false;
        if (filters.branches.length && !filters.branches.includes(order.branch?._id ?? "main")) return false;
        if (filters.number && String(order.orderNumber) !== filters.number.replace(/\D/g, "")) return false;
        if (filters.phone && !digitsOf(order.customerPhone).includes(digitsOf(filters.phone))) return false;
        return true;
    };

    useEffect(() => {
        const answer = data?.data;
        if (!answer) return;
        // Page one replaces the list; later pages extend it.
        setOrders((prev) => (answer.page === 1 ? answer.orders : [...prev, ...answer.orders]));
        setTotal(answer.total);
    }, [data]);

    // A different view or a different filter is a different list, so it starts again from page one.
    useEffect(() => {
        setPage(1);
        // Both belong to the list being replaced: what was waiting for it, and what had left it.
        setPending([]);
        setIsCountLost(false);
        setStaleIds([]);
        invoke({ query: queryFor(1) });
    }, [tab, filters]);

    useEffect(() => {
        if (page === 1) return;
        invoke({ query: queryFor(page) });
    }, [page]);

    // --- Handlers --------------------------------------------------------------------------------

    /**
     * Patched in place rather than refetched: the list may be many pages deep by now, and a card that
     * changes must not move under the person reading it.
     *
     * If the new values put the order outside the current filters the card is **kept** and marked
     * instead — a row vanishing while someone looks at it is the confusing part, and the dimmed card
     * with its "changed" tag is the only honest answer until the list is asked for again.
     */
    const patch = (updated: TyOrder) => {
        setOrders((prev) => prev.map((row) => (row._id === updated._id ? { ...row, ...updated } : row)));
        setStaleIds((prev) => {
            const isOut = !fitsView(updated);
            if (isOut === prev.includes(updated._id)) return prev;
            return isOut ? [...prev, updated._id] : prev.filter((id) => id !== updated._id);
        });
    };

    const handleEdit = (order: TyOrder) => {
        setActiveId(order._id);
        removeComp("order-edit");
        addComp({
            name: "order-edit",
            Component: OrderEditPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
            details: { order, onSaved: (updated: TyOrder) => patch(updated) },
        });
    };

    const handleOpen = (order: TyOrder) => {
        setActiveId(order._id);
        removeComp("order-preview");
        addComp({
            name: "order-preview",
            Component: OrderPreviewPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
            details: {
                order,
                editable,
                onUpdated: (updated: TyOrder) => patch(updated),
                // Correcting is a different job from watching, so the preview hands over rather than growing a form.
                onEdit: (current: TyOrder) => {
                    removeComp("order-preview");
                    handleEdit(current);
                },
            },
        });
    };

    /** A new order written down at the counter — the same form, with nothing in it yet. */
    const handleAdd = () => {
        removeComp("order-edit");
        addComp({
            name: "order-edit",
            Component: OrderEditPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
            details: {
                onSaved: (created: TyOrder, isNew: boolean) => {
                    if (!isNew) return patch(created);
                    setOrders((prev) => [created, ...prev]);
                    setTotal((prev) => prev + 1);
                    setActiveId(created._id);
                },
            },
        });
    };

    const handleAbort = (order: TyOrder) => {
        const next: TyOrderStatus = order.status === "pending" ? "rejected" : "cancelled";
        removeComp("confirm-order-status");
        addComp({
            name: "confirm-order-status",
            Component: ConfirmPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            details: {
                message: order.status === "pending" ? t.orders.rejectPrompt : t.orders.cancelPrompt,
                icon: "/icons/cancel-icon.svg",
                variant: "danger",
                confirmLabel: order.status === "pending" ? t.orders.rejectLabel : t.orders.cancelLabel,
                cancelLabel: t.orders.editCancelLabel,
                onConfirm: () => {
                    setActingId(order._id);
                    act({ method: "PUT", data: { status: next } });
                },
            },
        });
    };

    const [actingId, setActingId] = useState<string | null>(null);
    const {
        data: actionData,
        loading: acting,
        invoke: act,
    } = useFetch<TyApiResponse<{ order: TyOrder }>>(`/orders/${actingId ?? "x"}/status`, { method: "PUT" }, false);

    useEffect(() => {
        const updated = actionData?.data?.order;
        if (!updated) return;
        patch(updated);
        setActingId(null);
    }, [actionData]);

    /**
     * An order that was not here when the screen opened. It is counted and announced, never inserted —
     * and if this client is the one who just wrote it down, its own answer already put it in the list,
     * so the event is a patch rather than news.
     */
    const handleArrived = (order: TyOrder) => {
        if (orders.some((row) => row._id === order._id)) return patch(order);
        /* A number or a phone in the filter box is a lookup, not a watch: someone is answering a
           question about one order, and a count of everything else arriving is noise. */
        if (filters.number || filters.phone) return;
        if (!fitsView(order)) return;
        setPending((prev) => {
            if (prev.some((row) => row._id === order._id)) return prev;
            // Once per order, not once per render — the sound belongs to the arrival itself.
            if (isSoundOn) playOrderChime();
            return [...prev, order];
        });
    };

    /** Only orders already on screen are patched — one that is not here has no card to correct. */
    const handlePatched = (order: TyOrder) => {
        const shown = orders.find((row) => row._id === order._id);
        if (!shown) return;

        /**
         * A quieter sound than an arrival's, and only for a change this screen did not already know
         * about. Every write comes back twice — once as the answer to the request and once as the
         * event — and the second one must not make a noise about the counter's own tap.
         */
        const hasMoved =
            shown.status !== order.status ||
            shown.totalPrice !== order.totalPrice ||
            shown.items.length !== order.items.length ||
            (shown.edits?.length ?? 0) !== (order.edits?.length ?? 0);
        if (hasMoved && isSoundOn) playOrderUpdateChime();

        patch(order);
    };

    const { isWatching } = useOrderEvents({
        isLive,
        onArrived: handleArrived,
        onPatched: handlePatched,
        onResumed: () => setIsCountLost(true),
    });

    /** The only thing that moves the list: page one again, with the filters exactly as they stand. */
    const handleRefresh = () => {
        setPending([]);
        setIsCountLost(false);
        setStaleIds([]);
        setPage(1);
        invoke({ query: queryFor(1) });
    };

    /* Dismissing hides the announcement, not the orders — they arrive with the next fetch like any
       others, and a card already marked as changed keeps its tag, which is its own explanation. */
    const handleDismiss = () => {
        setPending([]);
        setIsCountLost(false);
    };

    /**
     * Whether new orders should make a noise, asked the first time this screen is opened and never
     * again — a counter that has to find a setting for it will never find it.
     *
     * The question is also what arms the sound: a browser refuses to play anything until the page
     * has been interacted with, so the audio context is created inside the click that answers yes.
     * Nothing is asked of the browser itself, so there is no permission to be denied — only a choice
     * to be remembered.
     */
    const handleSound = (isOn: boolean) => {
        if (isOn) armOrderChime();
        writeSound(isOn);
        setIsSoundOn(isOn);
    };

    useEffect(() => {
        if (isSoundOn !== null) return;
        removeComp("confirm-orders-sound");
        addComp({
            name: "confirm-orders-sound",
            Component: ConfirmPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            details: {
                message: t.orders.soundPrompt,
                icon: "/icons/notifications-icon.svg",
                variant: "primary",
                confirmLabel: t.orders.soundConfirm,
                cancelLabel: t.orders.soundDecline,
                onConfirm: () => handleSound(true),
                onDecline: () => handleSound(false),
            },
        });
    }, []);

    const handleToggleSound = () => handleSound(!isSoundOn);

    const handleToggleLive = () => {
        setIsLive((prev) => {
            const next = !prev;
            try {
                localStorage.setItem(LIVE_KEY, next ? "on" : "off");
            } catch {
                // Not being able to remember the choice is no reason to refuse it for this session.
            }
            if (!next) handleDismiss();
            return next;
        });
    };

    /**
     * Everything that can be done to an order, behind one button. The card used to carry a row of
     * them that changed with the status, which is what made one card taller than its neighbour.
     */
    const handleMore = (order: TyOrder, anchor: DOMRect) => {
        setActiveId(order._id);
        const isOver = order.status === "completed" || constants.orderAbortStatuses.includes(order.status as "rejected" | "cancelled");

        const name = `order-actions-${order._id}`;
        removeComp(name);
        addComp({
            name,
            Component: MoreActionsPopup,
            place: "anchor-below",
            anim: "scaleTop",
            cover: "invisible",
            coverClose: true,
            anchor: { top: anchor.top, left: anchor.left, right: anchor.right, bottom: anchor.bottom, width: anchor.width, height: anchor.height },
            details: {
                actions: [
                    { label: t.orders.previewLabel, icon: "/icons/eye-opened-icon.svg", handler: () => handleOpen(order) },
                    ...(editable && !isOver ? [{ label: t.orders.editLabel, icon: "/icons/edit-icon.svg", handler: () => handleEdit(order) }] : []),
                    ...(editable && !isOver
                        ? [
                              {
                                  label: order.status === "pending" ? t.orders.rejectLabel : t.orders.cancelLabel,
                                  icon: "/icons/cancel-icon.svg",
                                  handler: () => handleAbort(order),
                                  danger: true,
                              },
                          ]
                        : []),
                ],
            },
        });
    };

    const hasFilters = !!(filters.statuses.length || filters.branches.length || filters.range || filters.number || filters.phone);
    const hasMore = orders.length < total;

    const branchOptions = [
        // The store's own menu has no branch id, so it is named rather than identified.
        { value: "main", label: t.orders.mainMenu },
        ...(branchData?.success ? branchData.data.branches.map((branch) => ({ value: branch._id, label: branch.name })) : []),
    ];

    /** Applying trims the two typed fields; everything else is already a chosen value. */
    const apply = (next: TyFilters) => {
        setIsFiltering(false);
        setFilters({ ...next, number: next.number.trim(), phone: next.phone.trim() });
    };

    /**
     * What is narrowing the list right now, in the same words the pickers used. The bar's own button
     * only says *that* something is on; a short list under it says what, which is the difference
     * between "where did my orders go" and "ah, I am still on last week".
     */
    const appliedChips = [
        ...filters.statuses.map((entry) => ({ label: t.orders.filterStatusLabel, value: t.orders.status[entry as TyOrderStatus] })),
        ...filters.branches.map((entry) => ({
            label: t.orders.filterBranchLabel,
            value: branchOptions.find((option) => option.value === entry)?.label ?? entry,
        })),
        ...(filters.range
            ? [{ label: t.orders.filterDateLabel, value: t.orders[`date${filters.range[0].toUpperCase()}${filters.range.slice(1)}` as "dateToday"] }]
            : []),
        ...(filters.number ? [{ label: t.orders.searchNumber, value: `#${filters.number}` }] : []),
        ...(filters.phone ? [{ label: t.orders.searchPhone, value: filters.phone }] : []),
    ];

    /* Confirmed, because it throws away several deliberate choices at once and the list it leaves
       behind looks exactly like the one a wrong filter would have produced. */
    const handleClearFilters = () => {
        removeComp("confirm-clear-filters");
        addComp({
            name: "confirm-clear-filters",
            Component: ConfirmPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            details: {
                message: t.orders.clearFiltersPrompt,
                icon: "/icons/delete-icon.svg",
                variant: "danger",
                confirmLabel: t.orders.clearFiltersConfirm,
                cancelLabel: t.orders.editCancelLabel,
                onConfirm: () => {
                    setDraft(NO_FILTERS);
                    apply(NO_FILTERS);
                },
            },
        });
    };

    // --- Render ----------------------------------------------------------------------------------

    return (
        /* Anywhere that is not a card puts the list back to normal — the focus belongs to whatever
           is being worked on, and nothing is being worked on once attention leaves it. */
        <div
            className="orders-screen"
            onClick={(event) => {
                if ((event.target as HTMLElement).closest(".order-card")) return;
                setActiveId(null);
            }}
        >
            <ActionsBar
                tabs={TABS.map((entry) => ({ key: entry, label: entry === "all" ? t.orders.filterAll : (t.orders.type[entry] ?? entry) }))}
                activeTab={tab}
                onTab={(key) => setTab(key as "all" | TyOrderType)}
                addLabel={t.orders.newTitle}
                onAdd={editable ? handleAdd : undefined}
                isFiltering={isFiltering}
                hasFilters={hasFilters}
                onFilterOpen={() => {
                    setDraft(filters);
                    setIsFiltering(true);
                }}
                onFilterApply={() => apply(draft)}
                onFilterClear={() => {
                    setDraft(NO_FILTERS);
                    apply(NO_FILTERS);
                }}
                onFilterCancel={() => setIsFiltering(false)}
                filterContent={
                    <>
                        <FilterSearchInput
                            value={draft.number}
                            onChange={(number) => setDraft((prev) => ({ ...prev, number }))}
                            placeholder={t.orders.searchNumber}
                            width="sm"
                            onEnter={() => apply(draft)}
                        />
                        <FilterSearchInput
                            value={draft.phone}
                            onChange={(phone) => setDraft((prev) => ({ ...prev, phone }))}
                            placeholder={t.orders.searchPhone}
                            onEnter={() => apply(draft)}
                        />
                        <FilterMultiSelectInput
                            value={draft.statuses}
                            onChange={(statuses) => setDraft((prev) => ({ ...prev, statuses }))}
                            options={constants.orderStatuses.map((entry) => ({ value: entry, label: t.orders.status[entry as TyOrderStatus] }))}
                            anyLabel={t.orders.statusAll}
                        />
                        <FilterMultiSelectInput
                            value={draft.branches}
                            onChange={(branches) => setDraft((prev) => ({ ...prev, branches }))}
                            options={branchOptions}
                            anyLabel={t.orders.branchAll}
                        />
                        <FilterSelectInput
                            value={draft.range}
                            onChange={(range) => setDraft((prev) => ({ ...prev, range }))}
                            options={constants.orderDateRanges.map((entry) => ({
                                value: entry,
                                label: t.orders[`date${entry[0].toUpperCase()}${entry.slice(1)}` as "dateToday"],
                            }))}
                            anyLabel={t.orders.dateAll}
                        />
                    </>
                }
            />

            <div className="orders-screen-content">
                {appliedChips.length > 0 && (
                    <div className="orders-screen-applied">
                        <span className="orders-screen-applied-title">{t.orders.appliedTitle}</span>

                        <div className="orders-screen-applied-chips">
                            {appliedChips.map((chip) => (
                                <span key={`${chip.label}-${chip.value}`} className="orders-screen-applied-chip">
                                    <span className="orders-screen-applied-chip-label">{chip.label}</span>
                                    {chip.value}
                                </span>
                            ))}
                        </div>

                        <button type="button" className="orders-screen-applied-clear" onClick={handleClearFilters}>
                            {t.orders.clearFiltersLabel}
                        </button>
                    </div>
                )}

                {loading && orders.length === 0 && (
                    <div className="orders-screen-list">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <Skeleton key={i} className="orders-screen-skeleton" />
                        ))}
                    </div>
                )}

                {error && <p className="orders-screen-message orders-screen-message--error">{error}</p>}

                {!loading && !error && orders.length === 0 && (
                    <div className="orders-screen-empty">
                        <div className="orders-screen-empty-icon">
                            <img src="/icons/side-bar/menu-icon-dark.svg" alt="" width={32} height={32} />
                        </div>
                        <div className="orders-screen-empty-text">
                            <p className="orders-screen-empty-title">{t.orders.emptyTitle}</p>
                            <p className="orders-screen-empty-subtitle">{t.orders.emptySubtitle}</p>
                        </div>
                    </div>
                )}

                {orders.length > 0 && (
                    <div className="orders-screen-list">
                        {orders.map((order, i) => (
                            <OrderCard
                                key={order._id}
                                order={order}
                                index={i}
                                isActive={activeId === order._id}
                                isDimmed={!!activeId && activeId !== order._id}
                                isStale={staleIds.includes(order._id)}
                                busy={acting && actingId === order._id}
                                onOpen={() => handleOpen(order)}
                                onMore={(anchor) => handleMore(order, anchor)}
                            />
                        ))}
                    </div>
                )}

                {hasMore && (
                    <button type="button" className="orders-screen-more" onClick={() => setPage((prev) => prev + 1)} disabled={loading}>
                        {t.orders.loadMore}
                    </button>
                )}
            </div>

            {/* Deliberately not in the actions bar: that bar is where the counter works, and an
                interruption that lands on top of the controls gets clicked by accident. */}
            <OrdersLive
                isLive={isLive}
                isWatching={isWatching}
                onToggleLive={handleToggleLive}
                isSoundOn={isSoundOn}
                onToggleSound={handleToggleSound}
                newCount={isCountLost ? null : pending.length}
                hasChanged={staleIds.length > 0}
                onRefresh={handleRefresh}
                onDismiss={handleDismiss}
            />
        </div>
    );
}
